import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { FaExchangeAlt } from 'react-icons/fa';

const COLORS = {
  bg: '#f5f5db',
  brown: '#6d4e36',
  brownLight: '#8b5e3c',
  cream: '#fcfbf7',
  border: '#d8c9b8'
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    borderColor: state.isFocused ? COLORS.brownLight : COLORS.border,
    boxShadow: state.isFocused ? '0 0 0 0.15rem rgba(109, 78, 54, 0.12)' : 'none',
    ':hover': { borderColor: COLORS.brownLight },
    backgroundColor: '#fff'
  }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (base) => ({ ...base, zIndex: 99999, borderRadius: 10, overflow: 'hidden' }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? COLORS.brown : state.isFocused ? COLORS.bg : '#fff',
    color: state.isSelected ? '#fff' : COLORS.brown,
    cursor: 'pointer'
  }),
  placeholder: (base) => ({ ...base, color: '#8b6f58' }),
  singleValue: (base) => ({ ...base, color: COLORS.brown, fontWeight: 500 })
};

/**
 * Modal de transfert gasoil entre citernes.
 *
 * Props :
 *  - show          : boolean
 *  - onHide        : () => void
 *  - sourceCiternes: array — citernes déjà chargées et filtrées pour cet utilisateur (source)
 *  - onSuccess     : () => void — appelé après un transfert réussi pour rafraîchir les données
 */
const TransfertGasoilModal = ({ show, onHide, sourceCiternes = [], onSuccess }) => {
  const [allDest, setAllDest] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ source_citerne_id: '', dest_citerne_id: '', litres: '', commentaire: '' });

  useEffect(() => {
    if (!show) return;
    setForm({ source_citerne_id: '', dest_citerne_id: '', litres: '', commentaire: '' });
    setError('');
    setSuccess('');
    axios.get('/citernes-gasoil/pour-transfert')
      .then((res) => setAllDest(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAllDest([]));
  }, [show]);

  const sourceOptions = (sourceCiternes || [])
    .filter((c) => c?.id && c?.nom)
    .map((c) => ({
      value: Number(c.id),
      label: `${c.nom}${c.chantier?.name ? ` — ${c.chantier.name}` : ''} (Stock: ${Number(c.stock_reel_l || 0)} L)`
    }));

  const destOptions = (allDest || [])
    .filter((c) => String(c.id) !== String(form.source_citerne_id))
    .map((c) => ({
      value: Number(c.id),
      label: `${c.nom}${c.chantier?.name ? ` — ${c.chantier.name}` : ''} (Stock: ${Number(c.stock_reel_l || 0)} L)`
    }));

  const sourceValue = sourceOptions.find((o) => String(o.value) === String(form.source_citerne_id)) || null;
  const destValue = destOptions.find((o) => String(o.value) === String(form.dest_citerne_id)) || null;

  const handleSubmit = async () => {
    const litres = Number(String(form.litres).replace(',', '.'));
    if (!form.source_citerne_id) return setError('❌ Veuillez sélectionner une citerne source.');
    if (!form.dest_citerne_id) return setError('❌ Veuillez sélectionner une citerne destination.');
    if (Number(form.source_citerne_id) === Number(form.dest_citerne_id)) return setError('❌ Source et destination doivent être différentes.');
    if (!litres || litres <= 0) return setError('❌ Saisissez un nombre de litres positif.');

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const res = await axios.post('/citernes-gasoil/transfert', {
        source_citerne_id: Number(form.source_citerne_id),
        dest_citerne_id: Number(form.dest_citerne_id),
        litres,
        commentaire: form.commentaire || null
      });
      const { source, destination } = res.data;
      setSuccess(
        `✅ ${litres} L transférés de "${source.nom}" → stock ${source.stock_apres} L` +
        `\n→ "${destination.nom}" → stock ${destination.stock_apres} L` +
        (source.warning ? `\n⚠️ ${source.warning}` : '') +
        (destination.warning ? `\n⚠️ ${destination.warning}` : '')
      );
      setForm({ source_citerne_id: '', dest_citerne_id: '', litres: '', commentaire: '' });
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || '❌ Erreur lors du transfert.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton={!saving} style={{ backgroundColor: COLORS.bg, borderBottom: `1px solid ${COLORS.border}` }}>
        <Modal.Title style={{ color: COLORS.brown }}>
          <FaExchangeAlt className="me-2" />
          Transfert de Gasoil entre Citernes
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: COLORS.cream }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')} style={{ whiteSpace: 'pre-line' }}>
            {success}
          </Alert>
        )}

        <Form>
          <Row className="g-3">
            <Col md={12}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Citerne source (vos citernes) ⛽
              </Form.Label>
              <Select
                styles={selectStyles}
                options={sourceOptions}
                value={sourceValue}
                onChange={(sel) => setForm((p) => ({ ...p, source_citerne_id: sel ? sel.value : '', dest_citerne_id: '' }))}
                isClearable
                isSearchable
                placeholder="🔎 Sélectionner votre citerne source..."
                noOptionsMessage={() => 'Aucune citerne disponible'}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <Form.Text className="text-muted">Uniquement vos citernes (selon votre chantier affecté).</Form.Text>
            </Col>

            <Col md={12}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Citerne destination ⛽
              </Form.Label>
              <Select
                styles={selectStyles}
                options={destOptions}
                value={destValue}
                onChange={(sel) => setForm((p) => ({ ...p, dest_citerne_id: sel ? sel.value : '' }))}
                isClearable
                isSearchable
                placeholder="🔎 Sélectionner la citerne destination..."
                noOptionsMessage={() => 'Aucune citerne disponible'}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <Form.Text className="text-muted">N'importe quelle citerne active, tous chantiers confondus.</Form.Text>
            </Col>

            <Col md={6}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Quantité à transférer (L)
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={form.litres}
                onChange={(e) => setForm((p) => ({ ...p, litres: e.target.value }))}
                placeholder="Ex: 500"
                className="shadow-sm"
              />
            </Col>

            <Col md={6}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Commentaire (optionnel)
              </Form.Label>
              <Form.Control
                value={form.commentaire}
                onChange={(e) => setForm((p) => ({ ...p, commentaire: e.target.value }))}
                placeholder="Ex: approvisionnement chantier nord"
                className="shadow-sm"
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer style={{ backgroundColor: COLORS.cream }}>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Fermer
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
        >
          {saving ? 'Transfert en cours...' : <><FaExchangeAlt className="me-1" /> Transférer</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransfertGasoilModal;
